import AxiosInstance from '../config/AxiosInstance';

export interface Product {
    id: string;
    sku: string;
    type: number;
    productName: string;
    productGroup: { groupName: string };
    updatedAt: Date;
    isActive: boolean;
}

export interface ProductCorrectionsPayload {
    productId: string,
    /** Always positive; the line's reason adds or removes it. */
    quantity: number,
    /** Per unit of the line. */
    cost: number,
    reasonId: string,
    productUOMId?: string,
    comment?: string
}

export interface InventoryCorrectionsPayload {
    companyId: string,
    locationId: string,
    statusId: number,
    reasonId: string | null,
    comment?: string,
    inventoryCorrectionDetails: ProductCorrectionsPayload[]
}

const getLocations = async (userId: any, companyId: any, page: any, limit: any, searchValue: any) => {
    try {
        const { data } = await AxiosInstance.get('/api/location', {
            params: {
                user_id: userId,
                page: page,
                limit: limit,
                searchValue: searchValue
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
};

const createInventoryCorrections = async (payload: InventoryCorrectionsPayload) => {
    try {
        const { data } = await AxiosInstance.post('/api/inventory/inventory-corrections', payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

const updateInventoryCorrections = async (id: string, payload: InventoryCorrectionsPayload) => {
    try {
        const { data } = await AxiosInstance.put('/api/inventory/inventory-corrections/update/' + id, payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

const getListProductCorrections = async (page: any, limit: any, searchValue: any, inventoryCorrectionId: any, locationId: any) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/products/list-for-inventory-correction', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
                inventoryCorrectionId: inventoryCorrectionId ?? '',
                locationId: locationId ?? ''
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

const getListProductSupplierInventory = async (page: any, limit: any, searchValue: any, inventoryCorrectionId: any) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/products/list-for-supplier', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

const getListProductAttributes = async (page: any, limit: any, searchValue: any, attributeId: any) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/products/list-for-attribute', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
                attributeId: attributeId ?? ''
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

// searchValue matches name, display name, SKU or barcode; productGroupId narrows to one group.
const getListProductOrderTemplate = async (page: any, limit: any, searchValue: any, orderTemplateId: any, productGroupId?: string) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/products/list-for-order-template', {
            params: {
                page: page,
                limit: limit,
                searchValue: searchValue,
                orderTemplateId: orderTemplateId ?? '',
                ...(productGroupId ? { productGroupId } : {}),
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

const getListInventoryCorrections = async (page: string, limit: number) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/inventory-corrections/findByCreated', {
            params: {
                page: page,
                limit: limit,
                // searchValue: searchValue
            }
        });
        return { data };
    } catch (error) {
        throw error;
    }
}

const getDetailInventoryCorrections = async (id: string) => {
    try {
        const { data } = await AxiosInstance.get(`/api/inventory/inventory-corrections/get/${id}`);
        return { data };
    } catch (error) {
        throw error;
    }
}

const deleteInventoryCorrections = async (id: string) => {
    try {
        const { data } = await AxiosInstance.delete('/api/inventory/inventory-corrections/delete/' + id);
        return { data };
    } catch (error) {
        throw error;
    }
}

const approveInventoryCorrections = async (id: string) => {
    try {
        const { data } = await AxiosInstance.post(`/api/inventory/inventory-corrections/${id}/approve`);
        return { data };
    } catch (error) {
        throw error;
    }
}

const rejectInventoryCorrections = async (id: string) => {
    try {
        const { data } = await AxiosInstance.post(`/api/inventory/inventory-corrections/${id}/reject`);
        return { data };
    } catch (error) {
        throw error;
    }
}

const cancelInventoryCorrections = async (id: string) => {
    try {
        const { data } = await AxiosInstance.post(`/api/inventory/inventory-corrections/${id}/cancel`);
        return { data };
    } catch (error) {
        throw error;
    }
}

export interface CorrectionsOverviewQuery {
    from?: string;
    to?: string;
    status?: string;
    reasonId?: string;
    search?: string;
    locationId?: string;
    page: number;
    limit: number;
}

type Named = { id: string; name: string | null };

export interface CorrectionsOverviewRow {
    id: string;
    internalNumber: number;
    statusId: number;
    comment: string | null;
    location: Named | null;
    createdAt: string;
    createdBy: Named | null;
    approvedAt: string | null;
    approvedBy: Named | null;
    lines: number;
    /** Signed: what the lines add to (+) or take from (−) the stock value. */
    value: number;
    reasons: string[];
}

export interface CorrectionsOverview {
    rows: CorrectionsOverviewRow[];
    count: number;
    page: number;
    limit: number;
    statusCounts: Record<string, number>;
    byReason: { reasonId: string; name: string | null; lines: number; value: number }[];
    totals: { corrections: number; value: number };
    viewer: { canApprove: boolean };
}

const getCorrectionsOverview = async (query: CorrectionsOverviewQuery) => {
    try {
        const { data } = await AxiosInstance.get('/api/inventory/inventory-corrections/overview', { params: query });
        return { data };
    } catch (error) {
        throw error;
    }
}

export interface CorrectionLineCost {
    productId: string;
    productUOMId: string | null;
    /** Per unit of the line. */
    cost: number;
    source: 'stock' | 'supplier' | 'none';
    /** Only a 0.00 cost that no stock layer gives is entered by hand. */
    editable: boolean;
}

// The cost each line takes: the site's stock layers (FIFO), else the supplier; approval stores what was booked.
const getCorrectionLineCosts = async (payload: { locationId: string; lines: { productId: string; productUOMId?: string; quantity?: number; reasonId?: string }[] }) => {
    try {
        const { data } = await AxiosInstance.post('/api/inventory/inventory-corrections/costs', payload);
        return { data };
    } catch (error) {
        throw error;
    }
}

export {
    getCorrectionLineCosts,
    getCorrectionsOverview,
    getLocations,
    getListProductCorrections,
    createInventoryCorrections,
    updateInventoryCorrections,
    getListInventoryCorrections,
    getDetailInventoryCorrections,
    deleteInventoryCorrections,
    approveInventoryCorrections,
    rejectInventoryCorrections,
    cancelInventoryCorrections,
    getListProductOrderTemplate,
    getListProductAttributes,
    getListProductSupplierInventory
}